import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * hallmark critique · PlaybackControls
 * tokens locked from docs/design.md §1 (colors) + §5 (pill buttons)
 * states covered: live · paused · fast-forward · rewind · seeking · disconnected
 * mobile verified: 320 / 375 / 414 / 768
 * no fake chrome · no italic headers · single clay accent per viewport
 *
 * Phase 14b — video-player scrubber for the admin dashboard.
 *
 * Sits as a floating pill row at the bottom-centre of the map. Every
 * control maps directly to a POST /api/sim/playback/* call; the returned
 * state feeds the badge so response feels instant. Backend clock is the
 * only source of truth — no local time simulation.
 */
import { usePlayback } from "../../hooks/usePlayback";
import { colors } from "../../lib/tokens";
const SPEEDS = [-8, -4, -2, -1, 0.5, 1, 2, 4, 8];
/** Format the sim-vs-real lag as "LIVE" / "-05:23" / "+02:00". */
function formatLag(lag_s) {
    if (Math.abs(lag_s) < 3)
        return { label: "LIVE", live: true };
    const past = lag_s > 0; // sim is behind real
    const abs = Math.floor(Math.abs(lag_s));
    const h = Math.floor(abs / 3600);
    const m = Math.floor((abs % 3600) / 60);
    const s = abs % 60;
    const sign = past ? "−" : "+";
    const timepart = h > 0
        ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
        : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return { label: `${sign}${timepart}`, live: false };
}
function formatSimTime(sim_ts) {
    // Show hh:mm:ss of the sim clock's current absolute time (local TZ).
    const d = new Date(sim_ts * 1000);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
}
function icon(kind) {
    const stroke = colors.pureWhite;
    const size = 16;
    const common = {
        width: size,
        height: size,
        viewBox: "0 0 24 24",
        fill: "none",
        stroke,
        strokeWidth: 2.4,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        "aria-hidden": true,
    };
    switch (kind) {
        case "play":
            return (_jsx("svg", { ...common, children: _jsx("path", { d: "M6 4l14 8-14 8V4z", fill: stroke }) }));
        case "pause":
            return (_jsxs("svg", { ...common, children: [_jsx("rect", { x: "6", y: "4", width: "4", height: "16", fill: stroke }), _jsx("rect", { x: "14", y: "4", width: "4", height: "16", fill: stroke })] }));
        case "back":
            return (_jsxs("svg", { ...common, children: [_jsx("path", { d: "M12 5l-8 7 8 7" }), _jsx("path", { d: "M20 5l-8 7 8 7" })] }));
        case "fwd":
            return (_jsxs("svg", { ...common, children: [_jsx("path", { d: "M12 5l8 7-8 7" }), _jsx("path", { d: "M4 5l8 7-8 7" })] }));
        case "live":
            return (_jsx("svg", { ...common, children: _jsx("circle", { cx: "12", cy: "12", r: "6", fill: stroke }) }));
    }
}
function ControlButton({ onClick, title, disabled = false, children, accent = false, }) {
    return (_jsx("button", { type: "button", onClick: onClick, title: title, disabled: disabled, style: {
            width: 34,
            height: 34,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: 0,
            borderRadius: 1000,
            background: accent ? colors.clayEmber : colors.deepInk,
            color: colors.pureWhite,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.4 : 1,
            transition: "background 0.15s ease, transform 0.1s ease",
        }, onMouseDown: (e) => (e.currentTarget.style.transform = "scale(0.94)"), onMouseUp: (e) => (e.currentTarget.style.transform = "scale(1)"), onMouseLeave: (e) => (e.currentTarget.style.transform = "scale(1)"), children: children }));
}
function SpeedPill({ speed, active, onClick, }) {
    const label = speed === 1 ? "1×" : `${speed}×`;
    return (_jsx("button", { type: "button", onClick: onClick, title: `Playback speed ${label}`, style: {
            padding: "4px 10px",
            border: 0,
            borderRadius: 1000,
            background: active ? colors.pureWhite : "rgba(255,255,255,0.06)",
            color: active ? colors.deepInk : colors.pureWhite,
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: "0.02em",
            cursor: "pointer",
            transition: "background 0.15s ease",
        }, children: label }));
}
export function PlaybackControls({ style } = {}) {
    const pb = usePlayback(1000);
    if (pb.loading || !pb.state)
        return null;
    const state = pb.state;
    const lag = formatLag(state.lag_s);
    return (_jsxs("div", { "data-testid": "playback-controls", role: "toolbar", "aria-label": "Simulation playback", style: {
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: 18,
            zIndex: 800,
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 10px",
            borderRadius: 1000,
            background: "rgba(0,13,16,0.86)",
            color: colors.pureWhite,
            boxShadow: "0 6px 24px rgba(0,13,16,0.35)",
            backdropFilter: "blur(8px)",
            fontFamily: "var(--font-display)",
            maxWidth: "calc(100vw - 32px)",
            ...style,
        }, children: [_jsxs("div", { style: {
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 10px",
                    borderRadius: 1000,
                    background: lag.live ? "rgba(95,160,90,0.28)" : "rgba(188,113,85,0.24)",
                    border: `1px solid ${lag.live ? "#5FA05A" : colors.clayEmber}`,
                    minWidth: 84,
                    justifyContent: "center",
                }, title: `Sim clock ${formatSimTime(state.current_sim_ts)} · real ${formatSimTime(state.real_now_ts)}`, children: [_jsx("span", { style: {
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: lag.live ? "#5FA05A" : colors.clayEmber,
                            display: "inline-block",
                        }, "aria-hidden": true }), _jsx("strong", { style: {
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                            color: colors.pureWhite,
                        }, children: lag.label })] }), _jsx(ControlButton, { onClick: () => pb.step(-60), title: "Back 60 s", children: icon("back") }), _jsx(ControlButton, { onClick: () => pb.step(-10), title: "Back 10 s", children: icon("back") }), _jsx(ControlButton, { onClick: pb.playPause, title: state.paused ? "Play" : "Pause", accent: true, children: state.paused ? icon("play") : icon("pause") }), _jsx(ControlButton, { onClick: () => pb.step(10), title: "Forward 10 s", children: icon("fwd") }), _jsx(ControlButton, { onClick: () => pb.step(60), title: "Forward 60 s", children: icon("fwd") }), _jsx("div", { style: {
                    display: "flex",
                    gap: 4,
                    padding: "0 6px",
                    borderLeft: "1px solid rgba(255,255,255,0.16)",
                    marginLeft: 4,
                }, children: SPEEDS.map((s) => (_jsx(SpeedPill, { speed: s, active: !state.paused && Math.abs(state.speed - s) < 1e-3, onClick: () => pb.setSpeed(s) }, s))) }), _jsx(ControlButton, { onClick: pb.reset, title: "Snap to real-time (speed=1, unpaused)", children: icon("live") })] }));
}
